import React, {
  useMemo,
  useState,
  useContext,
  useCallback,
  useEffect,
} from 'react';

import { InfoCircleFill as InfoCircleFillIcon } from '@styled-icons/bootstrap/InfoCircleFill';
import { api } from 'services/api';
import { toast } from 'shared/toast';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  ModalFooter,
  FormControl,
  Input,
  Grid,
  Text,
  Heading,
  Select,
  FormErrorMessage,
  FormLabel,
  useMediaQuery,
  Checkbox,
  Tooltip,
  Icon,
  Flex,
} from '@chakra-ui/react';

import { ModalRootProps } from 'components/Modal/Root';

import AuthContext from 'contexts/AuthContext';

type FormErrors = {
  [key: string]: string | boolean;
  type: 'invalid' | 'required';
};

type Header = {
  [column: string]: string | number;
};

type Fields = {
  type_: string;
  code: string;
  composition_code: string;
  composition_type: string;
  class: string;
  description: string;
  unit_measure: string;
  unit_price: string;
  coefficient: string;
  untaxed_price: string;
  taxed_price: string;
};

interface CapabilityValue {
  id: number;
  capability_id: number;
  object_type: string;
  object_id: number;
  value: Fields;
  link?: string;
}

interface Capability {
  id: number;
  view: boolean;
  read_only: boolean;
  key: string;
  description: string;
  type: string;
  default_value?: string;
  capability_value?: CapabilityValue;
}

interface ModalCompositionImportFields extends ModalRootProps {
  data: {
    importId: number;
    mappedHeaders: Header;
    headerRow: number;
    baseId: number;
    baseName: string;
    versionName: string;
    fileName: string;
  };
}

const modalMarginHeight = 15;
const modalMobileFix = 100;

const ModalCompositionImportFields: React.FC<ModalCompositionImportFields> = ({
  onConfirm,
  handleClose,
  data: { importId, mappedHeaders, headerRow, baseName, versionName, fileName },
  ...restProps
}) => {
  const { user } = useContext(AuthContext);

  const [isMobile] = useMediaQuery('(max-width: 728px)');

  const [headers, setHeaders] = useState<Header>(mappedHeaders || {});
  const actualHeaders = useMemo(
    () => (headers ? Object.keys(headers) : []),
    [headers],
  );

  const [isUpdatingHeader, setIsUpdatingHeader] = useState(false);
  const [newHeaderLine, setNewHeaderLine] = useState(0);

  const [errors, setErrors] = useState<FormErrors>({} as FormErrors);
  const [loading, setLoading] = useState(false);
  const [capabilities, setCapabilities] = useState<Capability[]>([]);
  const [isSavingMapping, setIsSavingMapping] = useState(false);

  const [fields, setFields] = useState<Fields>({
    type_: '',
    code: '',
    composition_code: '',
    composition_type: '',
    class: '',
    description: '',
    unit_measure: '',
    unit_price: '',
    coefficient: '',
    untaxed_price: '',
    taxed_price: '',
  });

  const compositionImportConfigCapability = useMemo(() => {
    return capabilities.find(
      (cap) => cap.key === 'own_composition_import_config',
    )?.capability_value;
  }, [capabilities]);

  const columnsPayload = useMemo(() => {
    return {
      type_: fields.type_,
      code: fields.code,
      composition_code: fields.composition_code,
      composition_type: fields.composition_type,
      class: fields.class,
      description: fields.description,
      unit_measure: fields.unit_measure,
      unit_price: fields.unit_price,
      coefficient: fields.coefficient,
      untaxed_price: fields.untaxed_price,
      taxed_price: fields.taxed_price,
    };
  }, [fields]);

  const mappedColumns = useMemo(() => {
    return {
      type: fields.type_,
      code: fields.code,
      integration_code: null,
      composition_code: fields.composition_code,
      composition_type: fields.composition_type,
      class: fields.class,
      description: fields.description,
      unit_measure: fields.unit_measure,
      unit_price: fields.unit_price,
      coefficient: fields.coefficient,
    };
  }, [fields]);

  const getCapabilities = useCallback(async () => {
    setLoading(true);

    try {
      const { data: response } = await api.get('/capability', {
        params: {
          'filter[read_only]': 0,
          'filter[object_type]': 'organization',
          'filter[object_id]': user.managed_organization.organization.id,
        },
      });
      const capabilitiesLoaded = response.data;

      setCapabilities(capabilitiesLoaded);
    } catch (err) {
      toast({
        description:
          err.response?.data?.message ||
          'Houve um erro ao carregar as configurações!',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    getCapabilities();
  }, [getCapabilities]);

  const handleSaveMappingCapability = useCallback(async () => {
    setLoading(true);

    try {
      if (compositionImportConfigCapability) {
        await api.put(`/capability`, {
          object_type: 'organization',
          object_id: user.managed_organization.organization.id,
          data: [
            {
              id: compositionImportConfigCapability.id,
              value: columnsPayload,
            },
          ],
        });
      } else {
        await api.post('/capability', {
          object_type: 'organization',
          object_id: user.managed_organization.organization.id,
          data: [
            {
              key: 'own_composition_import_config',
              value: columnsPayload,
            },
          ],
        });
      }
    } catch (err) {
      toast({
        description:
          err.response?.data?.message ||
          'Houve um erro ao salvar as configurações de mapeamento',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [columnsPayload, user, compositionImportConfigCapability]);

  const handleSetSavedMapping = useCallback(() => {
    if (compositionImportConfigCapability) {
      const filteredData = Object.fromEntries(
        Object.entries(compositionImportConfigCapability.value).filter(
          ([, value]) => actualHeaders.includes(value),
        ),
      ) as Fields;

      setFields(filteredData);
    } else {
      toast({
        description: 'Nenhuma configuração de mapeamento salva encontrada.',
        status: 'info',
      });
    }
  }, [compositionImportConfigCapability, actualHeaders]);

  const handleUpdateHeader = async (): Promise<void> => {
    try {
      setLoading(true);

      const {
        data: {
          data: { mapped_header },
        },
      } = await api.put(`/import/${importId}`, {
        header_row: newHeaderLine,
      });

      setHeaders(mapped_header);
    } catch (err) {
      toast({
        description:
          err.response?.data?.message ||
          'Houve um erro inesperado ao atualizar a linha do cabeçalho',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = useCallback(async (): Promise<void> => {
    const formErrors: FormErrors = {} as FormErrors;

    if (!fields.type_) {
      formErrors.type_ = 'Tipo é obrigatório';
    }

    if (!fields.code) {
      formErrors.code = 'Código é obrigatório';
    }

    if (!fields.composition_code) {
      formErrors.composition_code = 'Código do serviço é obrigatório';
    }

    if (!fields.composition_type) {
      formErrors.composition_type = 'Grupo de serviço é obrigatório';
    }

    if (!fields.class) {
      formErrors.class = 'Tipo de insumos e serviços é obrigatório';
    }

    if (!fields.description) {
      formErrors.description = 'Descrição é obrigatório';
    }

    if (!fields.unit_measure) {
      formErrors.unit_measure = 'Unidade é obrigatório';
    }

    if (!fields.unit_price) {
      formErrors.unit_price = 'Preço é obrigatório';
    }

    if (!fields.coefficient) {
      formErrors.coefficient = 'Coeficiente é obrigatório';
    }

    setErrors(formErrors);
    if (Object.keys(formErrors).length) {
      return;
    }

    try {
      setLoading(true);

      const {
        data: {
          data: { id },
        },
      } = await api.put(`/import/${importId}`, {
        mapped_columns: mappedColumns,
        header_row: newHeaderLine || headerRow,
      });

      if (isSavingMapping) {
        await handleSaveMappingCapability();
        getCapabilities();
      }

      if (onConfirm) onConfirm(id);

      handleClose();
    } catch (err) {
      toast({
        description:
          err.response?.data?.message ||
          'Houve um erro inesperado ao importar o arquivo.',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [
    fields,
    mappedColumns,
    importId,
    newHeaderLine,
    headerRow,
    onConfirm,
    handleClose,
    isSavingMapping,
    handleSaveMappingCapability,
    getCapabilities,
  ]);

  return (
    <Modal scrollBehavior="inside" {...restProps}>
      <ModalOverlay />
      <ModalContent
        sx={{
          maxWidth: `750px`,
          height: `calc(100% - ${
            isMobile ? modalMarginHeight - modalMobileFix : modalMarginHeight
          }px)`,
          maxHeight: `calc(100% - ${
            isMobile ? modalMarginHeight - modalMobileFix : modalMarginHeight
          }px)`,
        }}
      >
        <ModalHeader>Configurar campos</ModalHeader>

        <ModalHeader
          mt="-6"
          fontSize="md"
          fontStyle="italic"
          display="flex"
          gap={2}
          flexWrap="wrap"
        >
          <Text>Base: {baseName}</Text>
          <Text>/</Text>
          <Text>Referência: {versionName}</Text>
          <Text>/</Text>
          <Text
            maxWidth="550px"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            isTruncated
            title={fileName}
          >
            Arquivo: {fileName}
          </Text>
        </ModalHeader>

        <ModalCloseButton />

        <ModalBody>
          <Heading as="h3" fontWeight="medium" fontSize="lg" mb={4}>
            Indique a coluna do arquivo importado que corresponda a cada campo
            do orçamento
          </Heading>

          <Grid
            w="100%"
            templateColumns="repeat(auto-fit, minmax(250px, 1fr))"
            columnGap={2}
            rowGap={4}
          >
            <Text fontWeight="bold" mb={4}>
              Orçamento
            </Text>
            <Text fontWeight="bold" mb={4}>
              Arquivo importado (Colunas)
            </Text>
          </Grid>

          <Grid
            w="100%"
            templateColumns="repeat(auto-fit, minmax(250px, 1fr))"
            columnGap={2}
            rowGap={4}
          >
            <FormControl>
              <Input
                type="text"
                value="Tipo (Serviço ou Insumo)"
                readOnly
                _readOnly={{ background: 'white' }}
              />
            </FormControl>

            <FormControl isInvalid={!!errors?.type_}>
              <Select
                value={fields.type_}
                onChange={(e) => {
                  setFields({ ...fields, type_: e.target.value });
                }}
              >
                <option value="">Selecione</option>
                {actualHeaders.map((column) => (
                  <option key={String(column)} value={column}>
                    {column}
                    {headerRow} - {headers[column]}
                  </option>
                ))}
              </Select>

              <FormErrorMessage>
                {!!errors?.type_ && 'Tipo é obrigatório'}
              </FormErrorMessage>
            </FormControl>

            <FormControl>
              <Input
                type="text"
                value="Código do item"
                readOnly
                _readOnly={{ background: 'white' }}
                size="md"
              />
            </FormControl>

            <FormControl isInvalid={!!errors?.code}>
              <Select
                value={fields.code}
                onChange={(e) => {
                  setFields({ ...fields, code: e.target.value });
                }}
              >
                <option value="">Selecione</option>
                {actualHeaders.map((column) => (
                  <option key={String(column)} value={column}>
                    {column}
                    {headerRow} - {headers[column]}
                  </option>
                ))}
              </Select>

              <FormErrorMessage>
                {!!errors?.code && 'Código do item é obrigatório'}
              </FormErrorMessage>
            </FormControl>

            <FormControl>
              <Input
                type="text"
                value="Código do serviço"
                readOnly
                _readOnly={{ background: 'white' }}
              />
            </FormControl>

            <FormControl isInvalid={!!errors?.composition_code}>
              <Select
                value={fields.composition_code}
                onChange={(e) => {
                  setFields({ ...fields, composition_code: e.target.value });
                }}
              >
                <option value="">Selecione</option>
                {actualHeaders.map((column) => (
                  <option key={String(column)} value={column}>
                    {column}
                    {headerRow} - {headers[column]}
                  </option>
                ))}
              </Select>

              <FormErrorMessage>
                {!!errors?.composition_code && 'Código é obrigatório'}
              </FormErrorMessage>
            </FormControl>

            <FormControl>
              <Input
                type="text"
                value="Grupo de serviço"
                readOnly
                _readOnly={{ background: 'white' }}
              />
            </FormControl>

            <FormControl isInvalid={!!errors?.composition_type}>
              <Select
                value={fields.composition_type}
                onChange={(e) => {
                  setFields({ ...fields, composition_type: e.target.value });
                }}
              >
                <option value="">Selecione</option>
                {actualHeaders.map((column) => (
                  <option key={String(column)} value={column}>
                    {column}
                    {headerRow} - {headers[column]}
                  </option>
                ))}
              </Select>

              <FormErrorMessage>
                {!!errors?.composition_type && 'Grupo de serviço é obrigatório'}
              </FormErrorMessage>
            </FormControl>

            <FormControl>
              <Input
                type="text"
                value="Tipo de insumo e serviços"
                readOnly
                _readOnly={{ background: 'white' }}
              />

              <br />
            </FormControl>

            <FormControl isInvalid={!!errors?.class}>
              <Select
                value={fields.class}
                onChange={(e) => {
                  setFields({ ...fields, class: e.target.value });
                }}
              >
                <option value="">Selecione</option>
                {actualHeaders.map((column) => (
                  <option key={String(column)} value={column}>
                    {column}
                    {headerRow} - {headers[column]}
                  </option>
                ))}
              </Select>

              <FormErrorMessage>
                {!!errors?.class && 'Tipo de insumo e serviços é obrigatório'}
              </FormErrorMessage>
            </FormControl>

            <FormControl>
              <Input
                type="text"
                value="Descrição"
                readOnly
                _readOnly={{ background: 'white' }}
              />
            </FormControl>

            <FormControl isInvalid={!!errors?.description}>
              <Select
                value={fields.description}
                onChange={(e) => {
                  setFields({ ...fields, description: e.target.value });
                }}
              >
                <option value="">Selecione</option>
                {actualHeaders.map((column) => (
                  <option key={String(column)} value={column}>
                    {column}
                    {headerRow} - {headers[column]}
                  </option>
                ))}
              </Select>

              <FormErrorMessage>
                {!!errors?.description && 'Descrição é obrigatório'}
              </FormErrorMessage>
            </FormControl>

            <FormControl>
              <Input
                type="text"
                value="Unidade"
                readOnly
                _readOnly={{ background: 'white' }}
              />
            </FormControl>

            <FormControl isInvalid={!!errors?.unit_measure}>
              <Select
                value={fields.unit_measure}
                onChange={(e) => {
                  setFields({ ...fields, unit_measure: e.target.value });
                }}
              >
                <option value="">Selecione</option>
                {actualHeaders.map((column) => (
                  <option key={String(column)} value={column}>
                    {column}
                    {headerRow} - {headers[column]}
                  </option>
                ))}

                <FormErrorMessage>
                  {!!errors?.unit_measure && 'Unidade de medida é obrigatório'}
                </FormErrorMessage>
              </Select>
            </FormControl>

            <FormControl>
              <Input
                type="text"
                value="Preço unitário"
                readOnly
                _readOnly={{ background: 'white' }}
              />
            </FormControl>

            <FormControl isInvalid={!!errors?.unit_price}>
              <Select
                value={fields.unit_price}
                onChange={(e) => {
                  setFields({ ...fields, unit_price: e.target.value });
                }}
              >
                <option value="">Selecione</option>
                {actualHeaders.map((column) => (
                  <option key={String(column)} value={column}>
                    {column}
                    {headerRow} - {headers[column]}
                  </option>
                ))}

                <FormErrorMessage>
                  {!!errors?.unit_price && 'Preço é obrigatório'}
                </FormErrorMessage>
              </Select>
            </FormControl>

            <FormControl>
              <Input
                type="text"
                value="Coeficiente"
                readOnly
                _readOnly={{ background: 'white' }}
              />
            </FormControl>

            <FormControl isInvalid={!!errors?.coefficient}>
              <Select
                value={fields.coefficient}
                onChange={(e) => {
                  setFields({ ...fields, coefficient: e.target.value });
                }}
              >
                <option value="">Selecione</option>
                {actualHeaders.map((column) => (
                  <option key={String(column)} value={column}>
                    {column}
                    {headerRow} - {headers[column]}
                  </option>
                ))}

                <FormErrorMessage>
                  {!!errors?.coefficient && 'Preço é obrigatório'}
                </FormErrorMessage>
              </Select>
            </FormControl>
          </Grid>

          <Checkbox
            mt={3}
            mr={1}
            isChecked={isSavingMapping}
            onChange={(e) => {
              setIsSavingMapping(e.target.checked);
            }}
            isDisabled={loading}
            colorScheme="blue"
          >
            Salvar configurações de mapeamento{' '}
            <Tooltip
              hasArrow
              label="As configurações de mapeamento serão salvas para que você possa carregar posteriormente e não precise configurá-las novamente na próxima importação."
            >
              <Icon as={InfoCircleFillIcon} className="icon" color="gray.700" />
            </Tooltip>
          </Checkbox>

          <Flex gap={4}>
            {compositionImportConfigCapability && (
              <Text
                cursor="pointer"
                _hover={{
                  textDecoration: 'underline',
                }}
                my={4}
                fontSize="sm"
                color="blue.500"
                onClick={() => {
                  handleSetSavedMapping();
                }}
              >
                Carregar configurações salvas
              </Text>
            )}

            <Text
              cursor="pointer"
              _hover={{
                textDecoration: 'underline',
              }}
              my={4}
              fontSize="sm"
              color="blue.500"
              onClick={() => {
                setIsUpdatingHeader(!isUpdatingHeader);
              }}
            >
              Modificar linha do cabeçalho
            </Text>
          </Flex>

          {isUpdatingHeader && (
            <>
              <FormControl mt={4}>
                <FormLabel>Selecione a nova linha do cabeçalho</FormLabel>
                <Input
                  type="number"
                  onChange={(e) => {
                    setNewHeaderLine(Number(e.target.value));
                  }}
                />
              </FormControl>

              <Button
                mt={4}
                colorScheme="blue"
                disabled={newHeaderLine === 0 || newHeaderLine < 0}
                isLoading={loading}
                onClick={async () => {
                  await handleUpdateHeader();
                  setIsUpdatingHeader(false);
                }}
              >
                Atualizar
              </Button>
            </>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            isLoading={loading}
            isDisabled={loading}
            colorScheme="green"
            onClick={handleConfirm}
          >
            Próximo
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalCompositionImportFields;
